import React, { useContext, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { Typography, Box, Icon } from "@mui/material";
import PaymentForm from "./PaymentForm.js";
import { useTheme } from "@emotion/react";
import { Helmet } from "react-helmet";
import CountdownTimer from "src/components/blocks/CountdownTimer.js";
import { useStripeCheckout } from "src/services/mutations/useStripeCheckout.js";
import { LoadingButton } from "../LoadingButton.js";
import { isStripeLoaded } from "src/components/StripeWrapper.js";
import { customEvent } from "src/utils/gtag.js";
import { rollbar } from "src/utils/ErrorBoundary/index.js";

const NativeCheckout = ({
  type = "consumer_annual",
  trialPeriodDays = 7,
  successUrl,
  cancelUrl,
}) => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const { mutate: stripeCheckout, isLoading: isStripeCheckoutLoading } =
    useStripeCheckout();

  // Track when Stripe fails to load
  useEffect(() => {
    if (!isStripeLoaded) {
      // Track in GA
      customEvent({
        name: "stripe_failed_to_load",
        category: "Payment",
        label: "Stripe Failed to Load in NativeCheckout",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          payment_type: type,
          trial_period_days: trialPeriodDays,
          user_agent: navigator.userAgent,
        },
      });

      // Track in Rollbar
      rollbar.warning("Stripe failed to load in NativeCheckout", {
        user_id: user?._id,
        user_email: user?.email,
        payment_type: type,
        trial_period_days: trialPeriodDays,
        user_agent: navigator.userAgent,
        location: window.location.href,
      });
    }
  }, [isStripeLoaded, user, type, trialPeriodDays]);

  const handleCheckout = () => {
    stripeCheckout(
      {
        type,
        successUrl,
        cancelUrl,
        freeTrial: trialPeriodDays > 0,
      },
      {
        onSuccess: (response) => {
          const { url } = response.data;
          if (url) {
            window.location.href = url;
          }
        },
        onError: (error) => {
          console.error("Stripe checkout error:", error);
        },
      }
    );
  };

  return (
    <>
      <Helmet>
        <title>Stanza - Payment Checkout</title>
      </Helmet>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          id="container"
          sx={{
            margin: "0 auto 0 auto",
            maxWidth: { xs: "100%", sm: "400px" },
            width: { xs: "-webkit-fill-available", sm: "100%" },
            position: "relative",
            textAlign: "center",
          }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              display: "block",
              margin: "24px auto",
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "3rem",
              mt: 0,
              mb: 1,
              fontWeight: 300,
            }}
          >
            verified_user
          </Icon>
          <Typography variant="title" sx={{ mb: 1 }}>
            Stanza+
          </Typography>
          {trialPeriodDays > 0 ? (
            <Box sx={{ mt: 0.5, mb: 4 }}>
              <Typography variant="h6">{trialPeriodDays} free days</Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: "text.primary", mt: 1 }}
              >
                then{" "}
                <span style={{ textDecoration: "line-through" }}>$5.00</span>{" "}
                $2.50/mo (billed annually)
              </Typography>
              <CountdownTimer />
            </Box>
          ) : (
            <Typography variant="h6" sx={{ mb: 3 }}>
              Subscribe to Stanza+
            </Typography>
          )}
          {isStripeLoaded ? (
            <PaymentForm
              type={type}
              trialPeriodDays={trialPeriodDays}
              successUrl={successUrl}
            />
          ) : (
            <>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleCheckout}
                loading={isStripeCheckoutLoading}
                fullWidth
              >
                {trialPeriodDays > 0
                  ? "Start 7-Day Free Trial"
                  : "Head to Checkout"}
              </LoadingButton>
            </>
          )}
        </Box>
      </div>
    </>
  );
};

export default NativeCheckout;
